import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Typography, Tooltip, message } from 'antd';

/**
 * Shared "Select Series to Open" / "Series Information" modal used by the PACS
 * and My Worklist pages.
 *
 * It owns everything that was previously duplicated across both pages:
 *   - fetching the study's series via QIDO-RS (`/studies/{uid}/series`)
 *   - mapping the DICOM tags (incl. (0020,1209) total and (0013,1003) synced)
 *   - selection state, select-all/deselect-all, row-click toggling
 *   - the modal shell + series table (Series No, Description, Modality, Body
 *     Part, Images "total / synced")
 *
 * Page-specific behaviour stays in the parent via props:
 *   - `infoOnly` renders a read-only listing (no checkboxes / open action).
 *   - `onOpenSeries` receives the picked series so the parent can launch the
 *     viewer however it needs (e.g. dual-monitor on PACS, plain window on
 *     My Worklist). Permission gating is done by the parent before opening.
 */

export interface SeriesRow {
  seriesUID: string;
  seriesDescription: string;
  modality: string;
  seriesNumber: number;
  seriesDate?: string;
  bodyPart: string;
  numInstances: number;
  syncedInstances: number | null;
}

interface SeriesSelectionModalProps {
  open: boolean;
  studyUID: string | null;
  record?: any;
  /** Read-only variant: lists series + counts, no selection / open action. */
  infoOnly?: boolean;
  onClose: () => void;
  /** Called with the selected series UIDs when the user clicks "Open". */
  onOpenSeries?: (studyUID: string, selectedSeriesUIDs: string[], record: any) => void | Promise<void>;
}

// Resolve a DICOMweb config URL (qidoRoot / wadoUriRoot) from the runtime config,
// falling back to the dev default when not configured.
function getConfigUrl(urlType: 'qidoRoot' | 'wadoUriRoot') {
  const config = (window as any).config;
  const dicomwebSource = config?.dataSources?.find(
    (source: any) => source.sourceName === 'dicomweb'
  );
  return (
    dicomwebSource?.configuration?.[urlType] ||
    `https://dev-api.photonpacs.com/dicom/aets/DCM4CHEE/${urlType === 'wadoUriRoot' ? 'wado' : 'rs'}`
  );
}

async function fetchDicomData(url: string) {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

// (0013,1003) IS — instances synced to central (local studies only).
// null when the tag is absent/empty/non-numeric (non-local or older responses)
// so we can distinguish "not reported" from a real count of 0.
function parseSyncedInstances(raw: any): number | null {
  if (raw == null || `${raw}`.trim() === '') {
    return null;
  }
  const n = parseInt(`${raw}`, 10);
  return Number.isNaN(n) ? null : n;
}

const SeriesSelectionModal: React.FC<SeriesSelectionModalProps> = ({
  open,
  studyUID,
  record,
  infoOnly = false,
  onClose,
  onOpenSeries,
}) => {
  const [seriesList, setSeriesList] = useState<SeriesRow[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !studyUID) {
      return;
    }

    let active = true;
    setLoading(true);
    setSeriesList([]);
    setSelectedSeries([]);

    (async () => {
      try {
        const qidoBase = getConfigUrl('qidoRoot');
        const seriesData = await fetchDicomData(`${qidoBase}/studies/${studyUID}/series`);

        const list: SeriesRow[] = seriesData
          .map((series: any) => ({
            seriesUID: series['0020000E']?.Value?.[0],
            seriesDescription: series['0008103E']?.Value?.[0] || 'Unknown Series',
            modality: series['00080060']?.Value?.[0] || 'OT',
            seriesNumber: parseInt(series['00200011']?.Value?.[0]) || 0,
            seriesDate: series['00080021']?.Value?.[0],
            bodyPart: series['00180015']?.Value?.[0] || '',
            numInstances: parseInt(series['00201209']?.Value?.[0]) || 0,
            syncedInstances: parseSyncedInstances(series['00131003']?.Value?.[0]),
          }))
          .filter((s: SeriesRow) => s.seriesUID) // drop series without a UID
          .sort((a: SeriesRow, b: SeriesRow) => a.seriesNumber - b.seriesNumber);

        if (!active) {
          return;
        }
        setSeriesList(list);
        setSelectedSeries(list.map(s => s.seriesUID)); // select all by default
      } catch (error: any) {
        if (active) {
          message.error(`Failed to fetch series: ${error.message}`);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, studyUID]);

  const toggleSeriesSelection = (seriesUID: string) => {
    setSelectedSeries(prev =>
      prev.includes(seriesUID) ? prev.filter(uid => uid !== seriesUID) : [...prev, seriesUID]
    );
  };

  const handleSelectAll = () => setSelectedSeries(seriesList.map(s => s.seriesUID));
  const handleDeselectAll = () => setSelectedSeries([]);

  const handleOpen = async () => {
    if (!studyUID || selectedSeries.length === 0) {
      message.warning('Please select at least one series');
      return;
    }
    await onOpenSeries?.(studyUID, selectedSeries, record);
    onClose();
  };

  const footer = infoOnly
    ? [
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]
    : [
        <Button key="select-all" onClick={handleSelectAll} disabled={loading}>
          Select All
        </Button>,
        <Button key="deselect-all" onClick={handleDeselectAll} disabled={loading}>
          Deselect All
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="open"
          type="primary"
          disabled={selectedSeries.length === 0 || loading}
          onClick={handleOpen}
        >
          Open ({selectedSeries.length} series)
        </Button>,
      ];

  return (
    <Modal
      title={infoOnly ? 'Series Information' : 'Select Series to Open'}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={900}
      style={{ top: 40 }}
      bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
    >
      <Table
        dataSource={seriesList}
        loading={loading}
        rowKey="seriesUID"
        pagination={false}
        scroll={{ y: 450 }}
        size="middle"
        rowSelection={
          infoOnly
            ? undefined
            : {
                type: 'checkbox',
                selectedRowKeys: selectedSeries,
                onChange: (selectedRowKeys: any[]) =>
                  setSelectedSeries(selectedRowKeys as string[]),
              }
        }
        onRow={
          infoOnly
            ? undefined
            : (row: SeriesRow) => ({
                onClick: () => toggleSeriesSelection(row.seriesUID),
                style: { cursor: 'pointer' },
              })
        }
        columns={[
          {
            title: 'Series No',
            dataIndex: 'seriesNumber',
            key: 'seriesNumber',
            width: 100,
            align: 'center',
            render: (num: number) => <Typography.Text strong>{num}</Typography.Text>,
          },
          {
            title: 'Description',
            dataIndex: 'seriesDescription',
            key: 'seriesDescription',
            ellipsis: true,
            render: (text: string) => text || 'No description',
          },
          {
            title: 'Modality',
            dataIndex: 'modality',
            key: 'modality',
            width: 110,
            align: 'center',
            render: (text: string) => text || '-',
          },
          {
            title: 'Body Part',
            dataIndex: 'bodyPart',
            key: 'bodyPart',
            width: 150,
            align: 'center',
            render: (text: string) => text || '-',
          },
          {
            title: (
              <Tooltip title="Total instances / Instances synced to central">
                <span>Images</span>
              </Tooltip>
            ),
            dataIndex: 'numInstances',
            key: 'numInstances',
            width: 120,
            align: 'center',
            render: (num: number, row: SeriesRow) => {
              const total = num > 0 ? num : 0;
              const synced = row?.syncedInstances;
              const syncedDisplay = synced && synced > 0 ? synced : '-';
              return (
                <Tooltip title={`Total: ${total} · Synced to central: ${syncedDisplay}`}>
                  <Typography.Text type={total === 0 ? 'secondary' : undefined}>
                    {`${total} / ${syncedDisplay}`}
                  </Typography.Text>
                </Tooltip>
              );
            },
          },
        ]}
        locale={{
          emptyText: 'No series found for this study',
        }}
      />
    </Modal>
  );
};

export default SeriesSelectionModal;
